import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvisibleCopilotDrawer } from '@/components/ArticleCreator/InvisibleCopilotDrawer';
import { LivePublishedPreview } from '@/components/ArticleCreator/LivePublishedPreview';
import { VisualDiffViewer } from '@/components/ArticleCreator/VisualDiffViewer';
import { InternalLinkPicker } from '@/components/ArticleCreator/InternalLinkPicker';
import { InfographicGeneratorModal } from '@/components/ArticleCreator/InfographicGeneratorModal';
import {
  copilotApi,
  type ArticleVersionItem,
  type CopilotErrorDetail,
  type DraftAnalysisResult,
  type InternalLinkSuggestion,
  type DiffResult,
  type NewsStorySchema,
} from '@/services/copilot';
import { Button } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { destinationsApi } from '@/services/config';
import { destinoPadrao, exigeEscolhaDeDestino } from '@/config/escopo';
import { ApiError } from '@/services/api';
import { detalheLegivel } from '@/services/erros';
import { useQuery } from '@tanstack/react-query';

import styles from './NewArticleCreator.module.css';

import { useLocation } from 'react-router-dom';

/**
 * F-487 — o editor de artigo resolve a marca pelo `?site=` da rota; sem ele a
 * sessão staff bate 400 e a tela abre em "Falha: 400" logo depois de a matéria
 * ter sido publicada COM SUCESSO. Hoje o `redirect_url` do backend vem sem
 * query e sem fragmento, mas separador e `#` são calculados para o dia em que
 * vier; se o destino já trouxer a marca, ela é respeitada.
 */
function comMarcaNaRota(destino: string, marca: string | undefined): string {
  if (!marca || /[?&]site=/.test(destino)) return destino;
  const [caminho, fragmento] = destino.split('#');
  const separador = caminho.includes('?') ? '&' : '?';
  const comQuery = `${caminho}${separador}site=${encodeURIComponent(marca)}`;
  return fragmento === undefined ? comQuery : `${comQuery}#${fragmento}`;
}

/**
 * F-442 - a capa E otimizada de verdade (Pillow/WebP, servida em /uploads),
 * mas `publish_article` chama o publicador do WordPress SEM `cover=`
 * (backend/app/api/v1/endpoints/copilot.py:428), entao a imagem destacada
 * nunca e anexada por este caminho. O botao continua na tela porque o upload
 * e a previa funcionam; o que nao existe e a ultima perna, e ela e dita aqui
 * em vez de o jornalista descobrir no post publicado sem capa.
 */
const AVISO_CAPA_NAO_SEGUE =
  'A capa ainda NÃO segue para o WordPress no publicar — suba a imagem ' +
  'destacada no Editor de artigo depois (em construção).';

export function NewArticleCreator() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // O site é o MESMO seletor do /artigos, não um canal paralelo. A versão
  // anterior lia `wireseo_active_site` do localStorage — chave que nenhum
  // lugar do sistema escrevia — e depois cravou o slug de um cliente como padrão: nome
  // de cliente fixo no código de uma tela multitenant. O padrão certo é
  // o primeiro site ativo DO TENANT, vindo de /destinations.
  const [site, setSite] = useState<string>(queryParams.get('site') || '');
  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 5 * 60_000,
  });
  // Sem padrao silencioso quando ha mais de um destino: ver config/escopo.ts.
  const currentSite = site || destinoPadrao(sites);
  const [draftId] = useState<string>(() => `draft-${Date.now()}`);

  // Raw Input & Context Documents State
  const [rawInput, setRawInput] = useState('');
  const [contextDocs, setContextDocs] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number }>>([]);

  // Article State (Left Input + Right Published Live Preview)
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [lead, setLead] = useState('');
  const [content, setContent] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string | undefined>();

  // Copilot Analysis & Execution State
  const [analysis, setAnalysis] = useState<DraftAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const [generationError, setGenerationError] = useState<{
    status: number;
    detail: CopilotErrorDetail | string;
  } | null>(null);
  const [lastStory, setLastStory] = useState<NewsStorySchema | null>(null);

  // Modals & Diffs State
  const [isInternalLinkOpen, setIsInternalLinkOpen] = useState(false);
  const [internalLinkSuggestions, setInternalLinkSuggestions] = useState<InternalLinkSuggestion[]>(
    [],
  );
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [internalLinkError, setInternalLinkError] = useState<string | null>(null);

  const [isInfographicOpen, setIsInfographicOpen] = useState(false);
  const [activeDiff, setActiveDiff] = useState<DiffResult | null>(null);
  const [isDiffOpen, setIsDiffOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessageRaw] = useState<string | null>(null);
  const [statusFalha, setStatusFalha] = useState(false);
  // Toda mensagem nasce neutra; só `setStatusDeFalha` pinta de erro. Envolver o
  // setter (em vez de trocar as ~15 chamadas) garante que um sucesso posterior
  // apaga o vermelho sozinho — sem isso a barra ficaria travada em falha.
  const setStatusMessage = (texto: string | null) => {
    setStatusFalha(false);
    setStatusMessageRaw(texto);
  };
  const setStatusDeFalha = (texto: string) => {
    setStatusFalha(true);
    setStatusMessageRaw(texto);
  };

  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const [versionList, setVersionList] = useState<ArticleVersionItem[]>([]);

  const handlePublishArticle = async () => {
    if (!title || (!content && !lead)) {
      alert('A matéria precisa ter pelo menos manchete e corpo para ser enviada para publicação.');
      return;
    }
    setIsSaving(true);
    setStatusMessage('Enviando para o funil único de publicação...');
    try {
      const res = await copilotApi.publishArticle({
        // R2 da re-análise: `lastStory ||` fazia toda edição pós-"Gerar com
        // IA" sumir em silêncio no publicar. Os CAMPOS VISÍVEIS são a verdade;
        // o story da geração entra só pelos metadados (trilha, fontes).
        story: {
          ...(lastStory || { trilha: 'hard_news' }),
          titulo: title,
          excerpt,
          lead,
          corpo_html: content,
        },
        site: currentSite,
        status: 'draft',
      });

      setStatusMessage('Matéria enviada para o WordPress com sucesso!');
      navigate(comMarcaNaRota(res.redirect_url || `/artigos/${res.post_id}`, currentSite));
    } catch (err) {
      console.error('Publish Error:', err);
      // F-384: `err.response.data.detail` é formato de axios; o cliente da casa
      // lança ApiError {status, body} e isso dava sempre undefined — o motivo
      // do backend (quota, destino sem credencial, WP fora) era descartado.
      alert(`Erro ao enviar matéria para publicação: ${detalheLegivel(err)}`);
      setStatusDeFalha('Erro na publicação.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenVersionsList = async () => {
    try {
      const versions = await copilotApi.listVersions(draftId);
      setVersionList(versions || []);
      setIsVersionsOpen(true);
    } catch (err) {
      console.error('Error fetching version history:', err);
      alert(`Não foi possível carregar o histórico de versões: ${detalheLegivel(err)}`);
    }
  };

  const handleRestoreVersion = (ver: ArticleVersionItem) => {
    if (ver.title) setTitle(ver.title);
    if (ver.lead) setLead(ver.lead);
    if (ver.content) setContent(ver.content);
    setIsVersionsOpen(false);
    setStatusMessage(`Versão #${ver.version_number} (${ver.version_type}) restaurada nos campos.`);
  };

  // Synchronize raw input to title/content if typed manually
  const handleRawInputChange = (text: string) => {
    setRawInput(text);
    if (!title && text.length > 0) {
      const lines = text.split('\n');
      const firstLine = lines[0].replace(/^#+\s*/, '');
      setTitle(firstLine.slice(0, 70));
      setContent(lines.slice(1).join('\n'));
    } else {
      setContent(text);
    }
  };

  // Background Debounced Intelligence Analysis
  useEffect(() => {
    if (!title && !lead && !content) return;

    const timer = setTimeout(async () => {
      setIsAnalyzing(true);
      try {
        const res = await copilotApi.analyze({ title, lead, content });
        setAnalysis(res);
      } catch (err) {
        console.error('Copilot analysis error:', err);
      } finally {
        setIsAnalyzing(false);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [title, lead, content]);

  // Ação 1: estruturar e gerar a matéria com IA (RAG + LLM).
  const handleGenerateParameterizedArticle = async () => {
    if (!rawInput.trim()) {
      alert('Digite um assunto, rascunho ou anexe um documento para estruturar a matéria.');
      return;
    }
    setIsGenerating(true);
    setGenerationError(null);
    setStatusMessage('Lendo o acervo e as fontes citadas no texto…');
    try {
      const res = await copilotApi.generateParameterizedArticle({
        raw_input: rawInput,
        context_docs: contextDocs,
        site: currentSite,
      });

      setLastStory(res);
      setTitle(res.titulo || res.title);
      setExcerpt(res.excerpt);
      setLead(res.lead);
      setContent(res.corpo_html || res.body_html);
      const modelName = res.geracao?.modelo || 'IA';
      setStatusMessage(`Matéria gerada com IA (Modelo: ${modelName}).`);

      // Save initial AI Proposal version
      copilotApi.saveVersion({
        draft_id: draftId,
        version_type: 'ai_proposal',
        title: res.titulo || res.title,
        lead: res.lead,
        content: res.corpo_html || res.body_html,
      });
    } catch (err) {
      console.error('Generative AI Error:', err);
      // O cliente HTTP da casa lanca ApiError {status, body} — nao um erro no
      // formato do axios. Ler err.response.* dava sempre undefined: o status
      // caia no fallback 502 (a tela dizia "HTTP 502" para um 422 real) e o
      // aviso_editorial do backend — com o motivo e as sugestoes de conserto —
      // era descartado, virando a string crua do erro. Medido em producao 27/08.
      const generico = (err ?? {}) as {
        status?: number;
        message?: string;
        response?: { status?: number; data?: unknown };
      };
      const status: number =
        err instanceof ApiError ? err.status : (generico.response?.status ?? generico.status ?? 0);
      const corpo: unknown = err instanceof ApiError ? err.body : generico.response?.data;
      const bruto: unknown =
        (corpo && typeof corpo === 'object' && 'detail' in corpo
          ? (corpo as { detail: unknown }).detail
          : corpo) ||
        generico.message ||
        'Falha na geração com IA';
      const detail: CopilotErrorDetail | string =
        typeof bruto === 'string'
          ? bruto
          : bruto && typeof bruto === 'object'
            ? (bruto as CopilotErrorDetail)
            : String(bruto);
      setGenerationError({ status, detail });
      setStatusMessage('Erro na geração com IA.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Ação 2: formatação determinística de H2/H3 e HTML, sem IA.
  const handleFormatTechnicalDraft = async () => {
    if (!rawInput.trim() && !content.trim()) {
      alert('Digite ou cole um texto para formatar.');
      return;
    }
    setIsFormatting(true);
    setStatusMessage('Formatando H2/H3 e aplicando as regras técnicas…');
    try {
      const res = await copilotApi.formatTechnicalDraft({
        raw_text: rawInput || content,
        headline_fallback: title,
      });

      setLastStory(res);
      setTitle(res.titulo || res.title);
      setLead(res.lead);
      setContent(res.corpo_html || res.body_html);
      setStatusMessage('Formatado tecnicamente sem IA (Zero chamadas / Zero custo).');

      copilotApi.saveVersion({
        draft_id: draftId,
        version_type: 'accepted_revision',
        title: res.titulo || res.title,
        lead: res.lead,
        content: res.corpo_html || res.body_html,
      });
    } catch (err) {
      console.error('Technical Format Error:', err);
      alert(`Falha ao aplicar a formatação técnica: ${detalheLegivel(err)}`);
      setStatusDeFalha('Erro na formatação.');
    } finally {
      setIsFormatting(false);
    }
  };

  // Document Upload Handler (PDF, DOCX, TXT, JSON, Audio/Video Ingestion)
  const handleFileUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.docx,.doc,.txt,.md,.json,.mp3,.wav,.mp4,.m4a,.webm,.mov,.ogg,.flac';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement | null)?.files?.[0];
      if (file) {
        setStatusMessage(`Processando ${file.name}...`);
        try {
          const res = await copilotApi.ingestDocument(file);
          setAttachedFiles((prev) => [...prev, { name: res.filename, size: res.size_bytes }]);
          setContextDocs(
            (prev) => `${prev}\n--- DOCUMENTO: ${res.filename} ---\n${res.text_content}`,
          );
          const extraCost = res.transcricao_usd ? ` (Custo áudio: US$ ${res.transcricao_usd})` : '';
          setStatusMessage(
            `Documento ${file.name} anexado como ${res.tipo_insumo || 'documento'}${extraCost}!`,
          );
        } catch (err) {
          console.error('Document Ingest Error:', err);
          setStatusDeFalha(`Erro ao anexar ${file.name}: ${detalheLegivel(err)}`);
        }
      }
    };
    input.click();
  };

  // Image Upload Handler (Cover Image 1200px Optimization)
  const handleImageUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement | null)?.files?.[0];
      if (file) {
        setStatusMessage(`Otimizando imagem de capa ${file.name}...`);
        try {
          const res = await copilotApi.optimizeImage(file);
          if (res.ok) {
            const finalUrl = res.url_persistida || URL.createObjectURL(file);
            setFeaturedImageUrl(finalUrl);
            setStatusMessage(
              `Capa otimizada (${res.final_width}x${res.final_height}px) — ela ainda não segue ` +
                'no publicar; suba a imagem destacada no Editor de artigo.',
            );
          } else {
            setStatusMessage(`Aviso de imagem: ${res.warning || res.error}`);
          }
        } catch (err) {
          console.error('Cover Image Error:', err);
          setStatusDeFalha(`Erro ao otimizar a capa: ${detalheLegivel(err)}`);
        }
      }
    };
    input.click();
  };

  // Internal Link Suggestions Picker Handler
  const handleOpenInternalLinkPicker = async () => {
    setIsLoadingLinks(true);
    setInternalLinkError(null);
    setIsInternalLinkOpen(true);
    try {
      const fullText = `${title}\n${lead}\n${content}`;
      const res = await copilotApi.suggestInternalLinks(fullText, currentSite);
      setInternalLinkSuggestions(res || []);
    } catch (err) {
      console.error('Error fetching internal links:', err);
      // F-384: a falha só ia para o console. O modal abria com a lista vazia e
      // o jornalista lia "nenhuma sugestão no acervo" onde houve erro de busca:
      // falha silenciosa disfarçada de resultado.
      setInternalLinkSuggestions([]);
      setInternalLinkError(
        detalheLegivel(err, 'Não foi possível buscar links internos no acervo.'),
      );
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const handleApplyInternalLink = (anchorText: string, targetUrl: string) => {
    const linkHtml = `<a href="${targetUrl}" target="_blank" rel="noopener noreferrer">${anchorText}</a>`;
    if (content.includes(anchorText)) {
      setContent(content.replace(anchorText, linkHtml));
    } else {
      setContent((prev) => `${prev}<p>Leia também: ${linkHtml}</p>`);
    }
    setStatusMessage(`Link interno inserido para "${anchorText}"`);
  };

  // Headline Proposal Handler
  const handleProposeHeadline = async (proposedHeadline: string) => {
    try {
      const diff = await copilotApi.proposeDiff(title, proposedHeadline);
      setActiveDiff(diff);
      setIsDiffOpen(true);
    } catch {
      setTitle(proposedHeadline);
    }
  };

  const handleAcceptDiff = (newText: string) => {
    setTitle(newText);
    copilotApi.saveVersion({
      draft_id: draftId,
      version_type: 'accepted_revision',
      title: newText,
      lead,
      content,
    });
    setStatusMessage('Manchete sugerida aceita.');
  };

  const handleInsertPaaQuestion = (question: string) => {
    const block = `<h3>${question}</h3><p>Saiba mais sobre a posição oficial e os desdobramentos...</p>`;
    setContent((prev) => `${prev}\n${block}`);
    setStatusMessage(`H3 da SERP "${question}" inserido.`);
  };

  const handleInsertInfographic = (infographicSnippet: string) => {
    const block = `<div class="infographic-block"><h4>Infográfico Estatístico</h4><p>${infographicSnippet}</p></div>`;
    setContent((prev) => `${prev}\n${block}`);
    setStatusMessage('Infográfico estatístico inserido.');
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      await copilotApi.saveVersion({
        draft_id: draftId,
        version_type: 'human_seed',
        title,
        lead,
        content,
      });
      setStatusMessage('Rascunho salvo no histórico de versões.');
    } catch (err) {
      console.error('Save draft error:', err);
      // setStatusMessage pinta como status neutro/ok — um erro ali parece
      // sucesso. Falha usa o canal de falha, com o motivo do backend (F-384).
      setStatusDeFalha(`Erro ao salvar rascunho: ${detalheLegivel(err)}`);
    } finally {
      setIsSaving(false);
    }
  };

  // O backend devolve `detail` como texto puro ou como objeto estruturado
  // (aviso editorial). Separar aqui evita acessar campo em string.
  const erroDetalhe =
    generationError && typeof generationError.detail === 'object'
      ? generationError.detail
      : undefined;
  const veredicto = erroDetalhe?.veredicto;
  const numerosInventados = veredicto?.numeros_inventados ?? [];
  const entidadesInventadas = veredicto?.entidades_inventadas ?? [];
  const dominiosNaoAutorizados = veredicto?.dominios_nao_autorizados ?? [];

  return (
    <div className={styles.root}>
      <header className={styles.head}>
        <button className={styles.back} onClick={() => navigate('/artigos')}>
          &larr; Voltar
        </button>
        <h1 className={styles.title}>Novo criador nativo</h1>
        <span className={styles.draftTag}>rascunho</span>
        <select
          className={styles.siteSelect}
          value={site}
          onChange={(e) => setSite(e.target.value)}
          aria-label="Site WordPress de destino"
          title="Site de destino: define o acervo dos links internos"
        >
          <option value="">
            {exigeEscolhaDeDestino(sites) ? '— escolha o destino —' : 'Site padrão'}
          </option>
          {(sites ?? []).map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.name}
            </option>
          ))}
        </select>

        <div className={styles.actions}>
          {statusMessage && (
            // A classe do CSS module pinta de sucesso; mensagem de falha
            // sobrescreve só a cor, pelo token de erro (nunca cor literal).
            <span
              className={styles.feedbackOk}
              style={statusFalha ? { color: 'var(--sig-crit-text)' } : undefined}
            >
              {statusMessage}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleGenerateParameterizedArticle}
            disabled={isGenerating}
          >
            {isGenerating ? 'Gerando com IA…' : 'Gerar com IA'}
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={handleFormatTechnicalDraft}
            disabled={isFormatting}
          >
            {isFormatting ? 'Formatando…' : 'Formatar técnico'}
          </Button>

          <Button size="sm" variant="secondary" onClick={handleSaveDraft} disabled={isSaving}>
            {isSaving ? 'Salvando…' : 'Salvar rascunho'}
          </Button>

          <Button size="sm" variant="primary" onClick={handlePublishArticle} disabled={isSaving}>
            Enviar para publicação
          </Button>
        </div>
      </header>

      {generationError &&
        (generationError.status === 422 || erroDetalhe?.tipo === 'aviso_editorial' ? (
          <div
            style={{
              padding: '14px 18px',
              margin: '16px 24px 0 24px',
              backgroundColor: 'var(--sig-warn-bg)',
              border: '1px solid var(--sig-warn-border)',
              borderRadius: 'var(--r-lg)',
              color: 'var(--sig-warn-text)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: '600',
                fontSize: 'var(--fs-lg)',
              }}
            >
              <span>{erroDetalhe?.titulo || 'Instruções para Enriquecer o Texto Original'}</span>
              <Button size="sm" variant="secondary" onClick={() => setGenerationError(null)}>
                Entendido
              </Button>
            </div>
            <p style={{ margin: '8px 0 4px 0', fontSize: '14px', lineHeight: '1.4' }}>
              {typeof generationError.detail === 'string'
                ? generationError.detail
                : erroDetalhe?.motivo ||
                  erroDetalhe?.erro ||
                  'O texto semente precisa de mais contexto ou fontes.'}
            </p>
            {erroDetalhe?.sugestoes && erroDetalhe.sugestoes.length > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  backgroundColor: 'var(--bg-0)',
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--sig-warn-border)',
                  fontSize: 'var(--fs-base)',
                }}
              >
                <strong
                  style={{ color: 'var(--sig-warn-text)', display: 'block', marginBottom: '6px' }}
                >
                  Sugestões de Ajuste no Texto de Entrada:
                </strong>
                <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-2)' }}>
                  {erroDetalhe.sugestoes.map((sug: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      {sug}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {veredicto && (
              <div style={{ marginTop: '8px', fontSize: 'var(--fs-sm)', color: 'var(--text-3)' }}>
                {numerosInventados.length > 0 && (
                  <div>• Números não confirmados no texto: {numerosInventados.join(', ')}</div>
                )}
                {entidadesInventadas.length > 0 && (
                  <div>
                    • Nomes/órgãos não encontrados na fonte: {entidadesInventadas.join(', ')}
                  </div>
                )}
                {dominiosNaoAutorizados.length > 0 && (
                  <div>• Links/Fontes não autorizadas: {dominiosNaoAutorizados.join(', ')}</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: '12px 16px',
              margin: '16px 24px 0 24px',
              backgroundColor: 'var(--sig-crit-bg)',
              border: '1px solid var(--sig-crit-border)',
              borderRadius: 'var(--r-md)',
              color: 'var(--sig-crit-text)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 'bold',
              }}
            >
              <span>
                {generationError.status === 422
                  ? 'O texto de entrada precisa de mais base factual'
                  : `Falha na geração com IA${generationError.status ? ` (HTTP ${generationError.status})` : ''}`}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleGenerateParameterizedArticle}
                disabled={isGenerating}
              >
                Tentar de novo
              </Button>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              {typeof generationError.detail === 'string'
                ? generationError.detail
                : generationError.detail?.erro ||
                  generationError.detail?.motivo ||
                  'Erro no processamento da IA.'}
            </p>
          </div>
        ))}

      {/* DUAL-CANVAS MAIN GRID */}
      <div className={styles.dualCanvasGrid}>
        {/* LEFT COLUMN: SMART INPUT EDITOR */}
        <main className={styles.leftColumn}>
          <div className={styles.fieldGroup}>
            <div className={styles.labelRow}>
              <label className={styles.fieldLabel}>Texto de entrada</label>
              <span className={styles.hint}>Texto colado, Markdown ou anotação de pauta</span>
            </div>
            <textarea
              className={styles.smartTextarea}
              placeholder="Digite livremente, cole o texto do Word/Markdown ou insira uma instrução de pauta..."
              value={rawInput}
              onChange={(e) => handleRawInputChange(e.target.value)}
            />
          </div>

          {/* ATTACHMENT BAR */}
          <div className={styles.attachmentBar}>
            <Button size="sm" variant="ghost" onClick={handleFileUploadClick}>
              Anexar PDF / DOCX
            </Button>
            {attachedFiles.map((f) => (
              <span key={f.name} className={styles.fileBadge}>
                {f.name} ({(f.size / 1024).toFixed(0)} KB)
              </span>
            ))}
          </div>

          {/* TOOLBAR AUXILIAR */}
          <div className={styles.toolbarRow} style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* O aviso fica COLADO no botao: quem sobe a capa le ali mesmo que
                ela nao viaja no publicar, sem depender de hover. */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 'var(--sp-1)',
                maxWidth: '270px',
              }}
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={handleImageUploadClick}
                title={AVISO_CAPA_NAO_SEGUE}
              >
                Capa 1200px
              </Button>
              <span
                className={styles.hint}
                style={{ color: 'var(--sig-warn-text)', lineHeight: 1.35 }}
              >
                {AVISO_CAPA_NAO_SEGUE}
              </span>
            </div>

            <Button size="sm" variant="ghost" onClick={handleOpenInternalLinkPicker}>
              Links internos
            </Button>

            <Button size="sm" variant="ghost" onClick={() => setIsInfographicOpen(true)}>
              Gerar infográfico
            </Button>

            <Button size="sm" variant="ghost" onClick={handleOpenVersionsList}>
              Histórico de versões
            </Button>
          </div>

          <InvisibleCopilotDrawer
            analysis={analysis}
            isLoading={isAnalyzing}
            onApplyHeadline={handleProposeHeadline}
            onInsertPaaQuestion={handleInsertPaaQuestion}
          />
        </main>

        {/* RIGHT COLUMN: LIVE PUBLISHED PREVIEW */}
        <aside className={styles.rightColumn}>
          {/* A previa e lida como contrato ("o que esta aqui e o que sai").
              Duas pecas dela nao saem: a capa (F-442) e o infografico (F-382).
              O subtitulo diz isso antes do publicar, nao depois. */}
          <div
            className={styles.labelRow}
            style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--sp-1)' }}
          >
            <span className={styles.fieldLabel}>Prévia do publicado</span>
            <span
              className={styles.hint}
              style={{ color: 'var(--sig-warn-text)', lineHeight: 1.4 }}
            >
              O texto desta prévia é o que publica; a capa e o infográfico ainda NÃO seguem para o
              WordPress (em construção).
            </span>
          </div>

          <LivePublishedPreview
            title={title}
            excerpt={excerpt}
            lead={lead}
            bodyHtml={content}
            imageUrl={featuredImageUrl}
            qualityGates={analysis?.quality_gates}
            generationMeta={lastStory?.geracao}
          />
        </aside>
      </div>

      {/* Visual Diff Viewer Modal */}
      <VisualDiffViewer
        isOpen={isDiffOpen}
        onClose={() => setIsDiffOpen(false)}
        diff={activeDiff}
        onAccept={handleAcceptDiff}
        onReject={() => setStatusMessage('Sugestão rejeitada.')}
      />

      {/* Internal Link Picker Modal */}
      {isInternalLinkOpen && internalLinkError ? (
        /* A falha ocupa o lugar EXATO onde a lista de sugestões apareceria —
           abrir o seletor vazio depois de um erro leria como acervo sem links. */
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'var(--bg-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-1)',
              border: '1px solid var(--border-2)',
              boxShadow: 'var(--shadow-3)',
              padding: '24px',
              borderRadius: 'var(--r-lg)',
              width: '550px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>Links internos do acervo</h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsInternalLinkOpen(false);
                  setInternalLinkError(null);
                }}
              >
                Fechar
              </Button>
            </div>
            <p
              style={{
                margin: '0 0 16px 0',
                color: 'var(--sig-crit-text)',
                fontSize: 'var(--fs-base)',
              }}
            >
              {internalLinkError}
            </p>
            <Button size="sm" variant="secondary" onClick={handleOpenInternalLinkPicker}>
              Tentar de novo
            </Button>
          </div>
        </div>
      ) : (
        <InternalLinkPicker
          isOpen={isInternalLinkOpen}
          onClose={() => setIsInternalLinkOpen(false)}
          suggestions={internalLinkSuggestions}
          isLoading={isLoadingLinks}
          onApplyLink={handleApplyInternalLink}
        />
      )}

      {/* Infographic Generator Modal */}
      <InfographicGeneratorModal
        isOpen={isInfographicOpen}
        onClose={() => setIsInfographicOpen(false)}
        articleTitle={title || 'Infográfico do Artigo'}
        onInsertInfographic={handleInsertInfographic}
      />

      {/* Version History Modal */}
      {isVersionsOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'var(--bg-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-1)',
              border: '1px solid var(--border-2)',
              boxShadow: 'var(--shadow-3)',
              padding: '24px',
              borderRadius: 'var(--r-lg)',
              width: '550px',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>Histórico de Versões do Rascunho</h3>
              <Button size="sm" variant="ghost" onClick={() => setIsVersionsOpen(false)}>
                Fechar
              </Button>
            </div>
            {versionList.length === 0 ? (
              <p style={{ color: 'var(--text-3)' }}>
                Nenhuma versão gravada ainda para este rascunho.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {versionList.map((v) => (
                  <li
                    key={v.id}
                    style={{
                      borderBottom: '1px solid var(--border-1)',
                      padding: '12px 0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <strong>
                        Versão #{v.version_number} ({v.version_type})
                      </strong>
                      <div
                        style={{
                          fontSize: 'var(--fs-base)',
                          color: 'var(--text-2)',
                          marginTop: '4px',
                        }}
                      >
                        {v.title || '(sem título)'}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-4)',
                          marginTop: '2px',
                        }}
                      >
                        {v.created_at ? formatarDataHora(v.created_at) : ''}
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => handleRestoreVersion(v)}>
                      Restaurar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
